import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DistributorAccess } from "@authentication/AuthenticationService";
import { selectDistributor } from "@authentication/selectDistributor";
import { detectPartnerFromHost } from "@partner/detect";

/**
 * DistributorSelectPage
 * ---------------------
 * Mirrors Merchant-Dashboard's MerchantSelectPage.handleMerchantSelect:
 * the user picks one of the distributors they have access to, we exchange
 * the no-scope token for a distributor-scoped token (via selectDistributor),
 * then navigate to /dashboard.
 */
export const DistributorSelectPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const partnerPreset = detectPartnerFromHost();

  const { distributorAccesses } = (location.state as any) || {
    distributorAccesses: [] as DistributorAccess[],
  };

  const handleDistributorSelect = async (distributor: DistributorAccess) => {
    try {
      await selectDistributor(distributor);
      navigate("/dashboard");
    } catch (error) {
      console.error("Error selecting distributor:", error);
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="w-2/5 relative flex items-start justify-start p-8 bg-cover bg-left"
        style={{ backgroundImage: `url(${partnerPreset.loginImgHref})` }}
      ></div>

      <div className="w-3/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-2xl space-y-8">
          <div className="flex flex-col items-center">
            <img
              src={partnerPreset.logoHref}
              alt="Logo"
              className="w-auto h-10 object-cover mb-6"
            />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Select Distributor
            </h2>
            <p className="text-gray-600">
              Choose which distributor dashboard you'd like to access
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {distributorAccesses.map((distributor: DistributorAccess) => (
              <button
                key={distributor.distributorId}
                onClick={() => handleDistributorSelect(distributor)}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all hover:border-neutral-300 flex flex-col text-left"
                aria-label={`Select ${distributor.distributorName} dashboard`}
                tabIndex={0}
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {distributor.distributorName}
                </h3>
                <p className="text-gray-600 text-sm">Click to access dashboard</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
